(function () {
  const DESKTOP_BREAKPOINT = 750;

  const renderBullets = (swiper, current, total) => {
    let out = "";
    for (let i = 1; i < total + 1; i++) {
      const active = i === current ? " swiper-pagination-bullet-active" : "";
      out += `<span class="swiper-pagination-bullet${active}" tabindex="0" role="button" aria-label="Go to slide ${i}"></span>`;
    }
    return out;
  };

  /* ---------- Grid slider ---------- */

  const initGridSlider = (root) => {
    const slider = root.querySelector("[data-lookbook-slider]");
    if (!slider || slider.swiperInstance || typeof Swiper === "undefined") return;

    const columns = Number(root.dataset.columns) || 3;
    const columnsMobile = Number(root.dataset.columnsMobile) || 1;
    const gap = parseInt(getComputedStyle(root).getPropertyValue("--lb-gap"), 10) || 24;
    const gapMobile =
      parseInt(getComputedStyle(root).getPropertyValue("--lb-gap-mobile"), 10) || 12;
    const slideCount = slider.querySelectorAll(".swiper-slide").length;

    slider.classList.add("swiper");

    const options = {
      loop: root.dataset.loop === "true" && slideCount > columns,
      speed: 600,
      watchOverflow: true,
      slidesPerView: columnsMobile,
      spaceBetween: gapMobile,
      breakpoints: {
        750: {
          slidesPerView: Math.min(2, columns),
          slidesPerGroup: 1,
          spaceBetween: gap,
        },
        990: {
          slidesPerView: Math.min(3, columns),
          slidesPerGroup: 1,
          spaceBetween: gap,
        },
        1200: {
          slidesPerView: columns,
          slidesPerGroup: 1,
          spaceBetween: gap,
        },
      },
    };

    const pagination = root.querySelector("[data-lookbook-pagination]");
    if (pagination) {
      options.pagination = {
        el: pagination,
        clickable: true,
        type: "custom",
        renderCustom: renderBullets,
      };
    }

    const prev = root.querySelector("[data-lookbook-prev]");
    const next = root.querySelector("[data-lookbook-next]");
    if (prev && next) {
      options.navigation = {
        prevEl: prev,
        nextEl: next,
        disabledClass: "swiper-button-disabled",
        lockClass: "swiper-button-lock",
      };
    }

    if (root.dataset.autoplay === "true") {
      options.autoplay = {
        delay: Number(root.dataset.delay) || 4000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }

    slider.swiperInstance = new Swiper(slider, options);
  };

  const destroyGridSlider = (root) => {
    const slider = root.querySelector("[data-lookbook-slider]");
    if (!slider) return;

    if (slider.swiperInstance) {
      slider.swiperInstance.destroy(true, true);
      slider.swiperInstance = null;
    }
    slider.classList.remove("swiper");
    slider.querySelectorAll(".swiper-slide").forEach((slide) => {
      slide.removeAttribute("style");
    });
  };

  /* ---------- Lightbox ---------- */

  const initModal = (root) => {
    let modal = root.querySelector("[data-lookbook-modal]");

    // The section root has `z-index: 1` via .spaced-section, which creates a
    // stacking context the fixed modal can never escape — it would paint
    // below the header and any later section. Move it to <body>.
    if (!modal) {
      modal = root.lookbookModal;
    }
    if (!modal) return;

    if (modal.parentElement !== document.body) {
      const id = modal.dataset.lookbookModal;
      document
        .querySelectorAll(`body > [data-lookbook-modal="${id}"]`)
        .forEach((stale) => stale.remove());
      document.body.appendChild(modal);
    }
    root.lookbookModal = modal;

    const container = modal.querySelector("[data-lookbook-modal-swiper]");
    let lastFocused = null;

    const buildSwiper = () => {
      if (modal.swiperInstance || typeof Swiper === "undefined") return;

      const options = {
        loop: modal.querySelectorAll(".swiper-slide").length > 1,
        speed: 500,
        slidesPerView: 1,
        spaceBetween: 24,
        watchOverflow: true,
        keyboard: { enabled: true },
      };

      const prev = modal.querySelector("[data-lookbook-modal-prev]");
      const next = modal.querySelector("[data-lookbook-modal-next]");
      if (prev && next) {
        options.navigation = {
          prevEl: prev,
          nextEl: next,
          disabledClass: "swiper-button-disabled",
          lockClass: "swiper-button-lock",
        };
      }

      modal.swiperInstance = new Swiper(container, options);
    };

    const open = (index, trigger) => {
      lastFocused = trigger || document.activeElement;

      modal.hidden = false;
      buildSwiper();

      // Force a reflow so the opening transition runs.
      void modal.offsetWidth;
      modal.classList.add("is-open");
      document.body.classList.add("overflow-hidden");

      if (modal.swiperInstance) {
        modal.swiperInstance.update();
        if (modal.swiperInstance.params.loop) {
          modal.swiperInstance.slideToLoop(index, 0);
        } else {
          modal.swiperInstance.slideTo(index, 0);
        }
      }

      const closeBtn = modal.querySelector("[data-lookbook-close]");
      if (closeBtn) closeBtn.focus();
    };

    const close = () => {
      modal.classList.remove("is-open");
      document.body.classList.remove("overflow-hidden");

      const done = () => {
        modal.hidden = true;
        modal.removeEventListener("transitionend", done);
      };
      modal.addEventListener("transitionend", done);
      // Fallback if the transition never fires.
      setTimeout(done, 400);

      if (lastFocused) lastFocused.focus();
    };

    root.querySelectorAll("[data-lookbook-open]").forEach((trigger) => {
      trigger.addEventListener("click", (event) => {
        event.preventDefault();
        open(Number(trigger.dataset.lookbookOpen) || 0, trigger);
      });
    });

    const closeBtn = modal.querySelector("[data-lookbook-close]");
    if (closeBtn) closeBtn.addEventListener("click", close);

    // Click the backdrop (but not the image or controls) to dismiss.
    modal.addEventListener("click", (event) => {
      if (event.target === modal || event.target.classList.contains("lookbook-modal__body")) {
        close();
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && modal.classList.contains("is-open")) {
        close();
      }
    });

    modal.destroyLookbook = () => {
      if (modal.swiperInstance) {
        modal.swiperInstance.destroy(true, true);
        modal.swiperInstance = null;
      }
      document.body.classList.remove("overflow-hidden");
    };
  };

  /* ---------- Section wiring ---------- */

  const initSection = (section) => {
    if (!section) return;

    section.querySelectorAll("[data-lookbook]").forEach((root) => {
      initModal(root);

      const slideDesktop = root.dataset.slideDesktop === "true";

      const observer = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const isDesktop = entry.contentRect.width >= DESKTOP_BREAKPOINT;

          // Mobile always slides; desktop only when there are more
          // items than columns.
          if (!isDesktop || slideDesktop) {
            initGridSlider(root);
          } else {
            destroyGridSlider(root);
          }
        });
      });

      observer.observe(root);
    });
  };

  initSection(document.currentScript.parentElement);

  document.addEventListener("shopify:section:load", (event) => {
    initSection(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll("[data-lookbook]").forEach((root) => {
      destroyGridSlider(root);
      const modal = root.lookbookModal;
      if (modal) {
        if (modal.destroyLookbook) modal.destroyLookbook();
        modal.remove();
      }
    });
  });
})();